/**
 * Small pure helpers shared by HUD components.
 */

import type { WireAura, WireSimState } from "@pit/protocol";
import { isShieldAura } from "../champions/auraIcons.js";

/**
 * Look up the own entity from a wire snapshot by seat.
 * Returns `null` if no snapshot or seat missing.
 */
export function ownEntity(
  snapshot: WireSimState | null,
  ownSeat: number | null,
): WireSimState["entities"][number] | null {
  if (snapshot === null || ownSeat === null) return null;
  const found = snapshot.entities.find((e) => e.seat === ownSeat);
  return found ?? null;
}

/**
 * Look up the target of the own entity's hostile slot.
 */
export function targetEntity(
  snapshot: WireSimState | null,
  ownSeat: number | null,
): WireSimState["entities"][number] | null {
  const me = ownEntity(snapshot, ownSeat);
  if (me === null) return null;
  const targetId = me.targets.hostile;
  if (targetId === null) return null;
  const found = snapshot?.entities.find((e) => e.entityId === targetId) ?? null;
  return found;
}

/**
 * Look up the target of the own entity's FRIENDLY (ally) slot (#333) — set
 * via `partyTarget`/`arenaTarget`-style ally selection. Unlike
 * `targetEntity` (hostile slot), this frequently resolves to the CASTER'S
 * OWN entity: `allyRoster` (packages/sim/src/targeting.ts) unconditionally
 * pins self as roster index 0, so in a 1v1 (no other allies) "party target
 * 1" always selects yourself. `FriendlyTargetFrame` is the HUD surface that
 * makes this visible.
 */
export function friendlyTargetEntity(
  snapshot: WireSimState | null,
  ownSeat: number | null,
): WireSimState["entities"][number] | null {
  const me = ownEntity(snapshot, ownSeat);
  if (me === null) return null;
  const targetId = me.targets.friendly;
  if (targetId === null) return null;
  const found = snapshot?.entities.find((e) => e.entityId === targetId) ?? null;
  return found;
}

/**
 * Ceil-of-ticks-per-second: seconds remaining until `endsAtTick` given
 * the current `latestTick` and tick rate. Non-negative; returns 0 when
 * expired. Matches the wp7 spec's "ceil of ticks/tickRate".
 */
export function ticksToSeconds(
  endsAtTick: number,
  latestTick: number,
  tickRate: number,
): number {
  const remaining = endsAtTick - latestTick;
  if (remaining <= 0) return 0;
  return Math.ceil(remaining / tickRate);
}

export function shieldAmount(auras: readonly WireAura[]): number {
  return auras.reduce((total, aura) => total + (isShieldAura(aura.def) ? aura.absorbRemaining : 0), 0);
}

export function shieldBarGeometry(
  hp: number,
  maxHp: number,
  shield: number,
): { readonly healthFraction: number; readonly shieldFraction: number; readonly shieldWrapFraction: number } {
  const healthFraction = hpFraction(hp, maxHp);
  const shieldFraction = maxHp <= 0 ? 0 : Math.max(0, shield / maxHp);
  const first = Math.min(shieldFraction, Math.max(0, 1 - healthFraction));
  const shieldWrapFraction = Math.max(0, healthFraction + shieldFraction - 1);
  const round = (value: number): number => Number(value.toFixed(12));
  return { healthFraction: round(healthFraction), shieldFraction: round(first), shieldWrapFraction: round(shieldWrapFraction) };
}

/** HP fraction in [0, 1]. */
export function hpFraction(hp: number, maxHp: number): number {
  if (maxHp <= 0) return 0;
  const f = hp / maxHp;
  if (f < 0) return 0;
  if (f > 1) return 1;
  return f;
}

/**
 * Progress fraction in [0, 1] for a CAST-TIME cast, or `null` when the entity
 * is not casting a cast-time spell (#192). Purely cosmetic client-side math.
 *
 * `null` (⇒ no bar) covers the non-cast-time cases:
 *   • `cast === null` — idle (the server emits `null` when the sim cast state
 *     is `"idle"`).
 *   • `endTick <= startTick` — an instant / off-GCD cast that resolves with no
 *     duration to fill, so there is no fillable bar.
 * Otherwise returns `elapsed / total`, clamped to [0, 1] (0 before the cast has
 * visibly begun, 1 once it should have resolved).
 */
export function castProgress(
  cast: WireSimState["entities"][number]["cast"],
  currentTick: number,
): number | null {
  if (cast === null) return null;
  const total = cast.endTick - cast.startTick;
  if (total <= 0) return null;
  const elapsed = currentTick - cast.startTick;
  if (elapsed <= 0) return 0;
  if (elapsed >= total) return 1;
  return elapsed / total;
}